import { useAuth } from '@/lib'
import { campaignsIndexQueryKey, campaignsShowQueryKey, useCampaignStatusChange } from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, Outlet, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo } from 'react'
import { useCampaignData, useIsAdmin, useIsJobillan, useJobillaOrgId } from '../../../../hooks'
import { CampaignNavConfig, CampaignWrapper, logger } from '../../../../ui'

export const Route = createFileRoute(
	'/_auth/company/$companyId/$campaignId',
)({
	component: RouteComponent,
})

function RouteComponent() {
	const jobillaOrgId = useJobillaOrgId()
	const { campaignId, companyId } = Route.useParams()
	const navigate = useNavigate()
	const queryClient = useQueryClient()

	const { can } = useAuth()
	const { campaign, isLoading } = useCampaignData({ campaignId })

	useEffect(() => {
		logger.setGroup('campaign', campaignId)
		return () => logger.setGroup('campaign', null)
	}, [campaignId])

	// Drafts live in the editor — but only send editors there. Users who can only
	// read (e.g. recruiters) stay on this view; otherwise they'd bounce back here
	// from the editor's own permission redirect, forming a loop.
	useEffect(() => {
		if (!isLoading && campaign.status === 'draft' && can('campaign:update')) {
			void navigate({
				to: '/company/$companyId/campaign/$campaignId',
				params: { companyId, campaignId },
				replace: true,
			})
		}
	}, [isLoading, campaign.status, can, navigate, companyId, campaignId])

	const campaignStatusChange = useCampaignStatusChange()

	const handlePullToDraft = useCallback(async () => {
		await campaignStatusChange.mutateAsync({ campaign: campaignId, data: { status: 'draft' } })

		// Invalidate queries to refresh data
		await Promise.all([
			queryClient.invalidateQueries({
				queryKey: campaignsShowQueryKey(campaignId),
			}),
			queryClient.invalidateQueries({
				queryKey: campaignsIndexQueryKey({
					'filter[organisation_id]': jobillaOrgId ?? undefined,
					'filter[is_archived]': 'false',
					sort: ['created_at'],
				}),
			}),
		])

		// Navigate to the editor
		await navigate({
			to: '/company/$companyId/campaign/$campaignId',
			params: { companyId, campaignId },
		})
	}, [campaignId, campaignStatusChange, companyId, navigate, jobillaOrgId, queryClient])

	const campaignNav: CampaignNavConfig = useMemo(() => ({
		overviewLinker: ({ campaignId, ...props }) => (
			<Link
				to="/company/$companyId/$campaignId/overview"
				params={{ companyId, campaignId }}
				{...props}
			/>
		),
		candidatesLinker: ({ campaignId, ...props }) => (
			<Link
				to="/company/$companyId/$campaignId/candidates"
				params={{ companyId, campaignId }}
				{...props}
			/>
		),
		targetingLinker: ({ campaignId, ...props }) => (
			<Link
				to="/company/$companyId/$campaignId/target"
				params={{ companyId, campaignId }}
				{...props}
			/>
		),
		analyticsLinker: ({ campaignId, ...props }) => (
			<Link
				to="/company/$companyId/$campaignId/analytics"
				params={{ companyId, campaignId }}
				{...props}
			/>
		),
		invoiceLinker: ({ campaignId, ...props }) => (
			<Link
				to="/company/$companyId/$campaignId/invoice"
				params={{ companyId, campaignId }}
				{...props}
			/>
		),
		accessLinker: ({ campaignId, ...props }) => (
			<Link
				to="/company/$companyId/$campaignId/access"
				params={{ companyId, campaignId }}
				{...props}
			/>
		),
		exportLinker: ({ campaignId, ...props }) => (
			<Link
				to="/company/$companyId/$campaignId/export"
				params={{ companyId, campaignId }}
				{...props}
			/>
		),
		settingsLinker: ({ campaignId, ...props }) => (
			<Link
				to="/company/$companyId/$campaignId/settings"
				params={{ companyId, campaignId }}
				{...props}
			/>
		),
	}), [companyId])

	const isAdmin = useIsAdmin()
	const isJobillan = useIsJobillan()

	const permissions = useMemo(() => ({
		canApprove: isJobillan,
		canConfigureTargeting: isJobillan && campaign.status === 'review',
		canSeeAnalytics: ['completed', 'active', 'review'].includes(campaign.status),
		canSeeInvoice: isJobillan,
		canSeeExport: isJobillan || isAdmin,
	}), [campaign.status, isAdmin, isJobillan])

	return (
		<CampaignWrapper
			campaignNav={campaignNav}
			campaign={campaign}
			isLoading={isLoading}
			permissions={permissions}
			onPullToDraft={campaign.status === 'review' ? handlePullToDraft : undefined}
		>
			<Outlet />
		</CampaignWrapper>
	)
}
